import { refuseUnported } from '../janus-engine/unported';
import { markStall } from './stall-mark';

/** Above the measured 150.8 s cold start. Progress restarts this idle clock. */
export const COMBAT_IDLE_MS = 180_000;
export type CombatProgress = (stage: string, posted?: boolean) => void;

/** Only planning can expire. Once sent, transport owns its 60 s timeout and receipt retry. */
export async function withCombatProgress<T>(run: (signal: AbortSignal, progress: CombatProgress) => Promise<T>, parent?: AbortSignal, report?: CombatProgress): Promise<T> {
    const controller = new AbortController();
    const abort = (): void => { controller.abort(parent?.reason); };
    let timer: ReturnType<typeof setTimeout> | undefined;
    let posted = false;
    const progress: CombatProgress = (stage, sent = false) => {
        controller.signal.throwIfAborted();
        clearTimeout(timer);
        posted ||= sent;
        if (posted) parent?.removeEventListener('abort', abort);
        else timer = setTimeout(() => controller.abort(new DOMException('Combat preparation stopped progressing', 'TimeoutError')), COMBAT_IDLE_MS);
        markStall('combat:' + stage);
        report?.(stage, posted);
    };
    let expired!: () => void;
    const cancelled = new Promise<never>((_, reject) => {
        expired = () => reject(controller.signal.reason);
        controller.signal.addEventListener('abort', expired, { once: true });
    });
    parent?.addEventListener('abort', abort, { once: true });
    if (parent?.aborted) abort();
    try {
        return await Promise.race([cancelled, Promise.resolve().then(() => {
            controller.signal.throwIfAborted();
            progress('Preparing combat…');
            return run(controller.signal, progress);
        })]);
    } finally {
        clearTimeout(timer);
        parent?.removeEventListener('abort', abort);
        controller.signal.removeEventListener('abort', expired);
    }
}

export function refuseCombatChange(error: unknown, detail: Record<string, unknown>): null {
    const timeout = error instanceof DOMException && error.name === 'TimeoutError';
    refuseUnported(timeout ? 'combat-change-timeout' : 'combat-change-failed', { ...detail, error: String(error) },
        timeout
            ? 'Combat preparation stopped progressing before anything was saved. Try again.'
            : 'The combat change failed. Try again; if it keeps failing, reload the page.');
    return null;
}
